import React, { Suspense, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Loader2, ShieldCheck, Sparkles, Zap } from 'lucide-react';

import { Navbar } from './Navbar';
import { MobileBottomNav } from './MobileBottomNav';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/use-i18n';
import { useAuth } from '@/hooks/use-auth';

function MetadataManager() {
  const { t, lang } = useI18n();
  const location = useLocation();

  useEffect(() => {
    const brand = t('brand');
    const path = location.pathname.substring(1);
    const pageName = path ? path.split('/')[0].charAt(0).toUpperCase() + path.split('/')[0].slice(1) : 'Home';
    const description = 'Prompt STV 2026 - thư viện prompt AI với giao diện sáng cao cấp, mobile-first và tối ưu copy nhanh.';

    document.title = `${brand} - ${pageName}`;

    const ensureMeta = (attribute: 'name' | 'property', value: string) => {
      let meta = document.querySelector(`meta[${attribute}="${value}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
      return meta;
    };

    ensureMeta('name', 'description').setAttribute('content', description);
    ensureMeta('property', 'og:title').setAttribute('content', `${brand} - ${pageName}`);
    ensureMeta('property', 'og:description').setAttribute('content', description);
  }, [location.pathname, lang, t]);

  return null;
}

export function AppLayout() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const initializeAuth = useAuth((s) => s.initialize);
  const isInitialized = useAuth((s) => s.isInitialized);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  if (!isInitialized) {
    return (
      <div className="relative flex min-h-[100dvh] items-center justify-center overflow-x-hidden bg-[#F8FAFC] text-slate-900">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.12),transparent_26%),radial-gradient(circle_at_82%_18%,rgba(20,184,166,0.10),transparent_22%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]" />
        <div className="pointer-events-none absolute inset-0 hero-grid opacity-30" />
        <div className="relative flex flex-col items-center gap-4 rounded-[28px] border border-slate-200 bg-white/90 px-8 py-7 shadow-[0_10px_25px_-5px_rgba(148,163,184,0.15)] backdrop-blur-2xl">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-teal-50 text-cyan-500 shadow-[0_0_40px_rgba(34,211,238,0.16)]">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
          <div className="space-y-1 text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">Prompt STV 2026</div>
            <p className="text-sm text-slate-600">Đang tải giao diện sáng cao cấp.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden bg-background text-foreground selection:bg-cyan-100 selection:text-slate-900">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.10),transparent_24%),radial-gradient(circle_at_80%_18%,rgba(20,184,166,0.08),transparent_22%),linear-gradient(180deg,#ffffff_0%,#f8fafc_54%,#f1f5f9_100%)]" />
      <div className="pointer-events-none absolute inset-0 hero-grid opacity-20" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.14),transparent_68%)] blur-3xl" />

      <div className="relative z-10 flex min-h-[100dvh] flex-col">
        <MetadataManager />
        <Navbar />

        <main className="flex-1 pb-40 md:pb-0">
          <Suspense
            fallback={
              <div className="flex h-[50vh] items-center justify-center px-4">
                <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white/90 px-4 py-3 text-sm text-slate-600 shadow-[0_10px_25px_-5px_rgba(148,163,184,0.15)] backdrop-blur-xl">
                  <Loader2 className="h-4 w-4 animate-spin text-cyan-500" />
                  <span>Đang tải nội dung.</span>
                </div>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>

        <footer className="relative z-10 border-t border-slate-200/80 bg-white/70">
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
            <div className="rounded-[30px] border border-slate-200 bg-white/88 p-5 shadow-[0_10px_25px_-5px_rgba(148,163,184,0.15)] backdrop-blur-2xl lg:p-8">
              <div className="grid gap-8 lg:grid-cols-[1.25fr_0.85fr] lg:gap-12">
                <div className="space-y-5">
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
                    <Sparkles className="h-3.5 w-3.5" />
                    Prompt STV 2026
                  </div>
                  <div className="space-y-3">
                    <h2 className="max-w-sm text-2xl font-black tracking-tight text-slate-900 md:text-3xl">
                      Prompt thông minh, giao diện sáng, trải nghiệm cao cấp.
                    </h2>
                    <p className="max-w-md text-sm leading-6 text-slate-600">{t('footer.desc')}</p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      onClick={() => navigate('/browse')}
                      className="h-11 rounded-full bg-gradient-to-r from-[#14B8A6] to-[#22D3EE] px-5 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(34,211,238,0.22)] hover:opacity-95"
                    >
                      Khám phá ngay
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/submit')}
                      className="h-11 rounded-full border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Tạo prompt
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                      Tối ưu mobile-first
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                      <Zap className="h-3.5 w-3.5 text-cyan-500" />
                      Copy prompt siêu nhanh
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-500">Điều hướng</h3>
                  <div className="grid gap-2 text-sm text-slate-700">
                    <Link to="/browse" className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 transition hover:border-cyan-200 hover:bg-cyan-50/40">
                      <span>Khám phá</span>
                      <ChevronRight className="h-4 w-4 text-cyan-500" />
                    </Link>
                    <Link to="/submit" className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 transition hover:border-cyan-200 hover:bg-cyan-50/40">
                      <span>Tạo prompt</span>
                      <ChevronRight className="h-4 w-4 text-cyan-500" />
                    </Link>
                    <Link to="/profile" className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 transition hover:border-cyan-200 hover:bg-cyan-50/40">
                      <span>Tài khoản</span>
                      <ChevronRight className="h-4 w-4 text-cyan-500" />
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-8 grid gap-4 border-t border-slate-200 pt-5 text-sm text-slate-500 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="flex flex-wrap items-center gap-2">
                  <span>© {new Date().getFullYear()} Prompt.stv.vn.</span>
                  <span>{t('footer.rights')}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">AI Prompt Library</span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">Crystal Light Premium</span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">Mobile-first</span>
                </div>
              </div>
            </div>
          </div>
        </footer>

        <MobileBottomNav />
        <Toaster richColors closeButton />
      </div>
    </div>
  );
}
